/**
 * N8 (86d3w2kez) part 1 — where a notification points, as a pure function.
 *
 * ⚠️ **THERE IS NO UNIFORM `/x/:id` IN THIS APP, and assuming one 404s two of the four flows.**
 * Sales orders and cutting lists live on WORKSPACE pages addressed by a `?id=` query parameter;
 * material issuance and item receipts are ordinary path routes. A naive `/${type}/${id}` builder
 * looks right, passes review, and dead-ends every SO and CL notification in the product.
 *
 * Verified against `src/router/index.tsx` (2026-08-25): `/quotations/workspace` (:189),
 * `/sales-orders/workspace` (:203), `/mro/receiving/:id` (:214), `/mro/issuance/picklist/:id` (:235).
 */

/** The posting flows, exactly as `NotificationDto.Type` spells them. */
const ROUTES: Record<string, (id: string) => string> = {
  'posting.so': (id) => `/sales-orders/workspace?id=${encodeURIComponent(id)}`,
  'posting.cl': (id) => `/quotations/workspace?id=${encodeURIComponent(id)}`,
  'posting.mis': (id) => `/mro/issuance/picklist/${encodeURIComponent(id)}`,
  'posting.ir': (id) => `/mro/receiving/${encodeURIComponent(id)}`,
};

/**
 * The record a notification is about, or null when there is nowhere honest to send someone.
 *
 * ⛔ Returns null rather than guessing. An unknown type means the server's vocabulary grew ahead of
 * this client (Comments, procurement), and a fabricated URL would land the user on a 404 that looks
 * like the record was deleted. The caller renders a non-navigating row and says so.
 */
export function entityHrefFor(type: string, entityId: string | null): string | null {
  if (!entityId) return null;

  const build = ROUTES[type];
  return build ? build(entityId) : null;
}

/**
 * The same href with the one-shot arrival marker attached, so the landing page knows to flash the
 * posting surface rather than dumping the user at the top of a long record.
 *
 * ⚠️ Built through `URL`/`URLSearchParams` rather than string concatenation, because two of the four
 * routes ALREADY carry a query string — `?id=`. A naive `href + '?flash=' + jobId` produces
 * `?id=7?flash=42`, which parses as an id of `7?flash=42` and silently selects nothing.
 */
export function notificationHref(n: {
  type: string;
  entityId: string | null;
  jobId: number | null;
  actions?: readonly string[];
}): string | null {
  const base = entityHrefFor(n.type, n.entityId);
  if (!base) return null;

  // ⚠️ The marker rides ONLY rows that still need a decision. Its sole consumer —
  // `PostingArrivalSurface` — mounts while a posting job is active or failed, so on a settled row
  // (posted / cancelled / done) nothing ever consumes or strips it: the parameter would sit in the
  // URL, survive the workspaces' param-preserving rewrites, and ride along when the user copies the
  // link — arming a highlight for whoever opens it next (2026-08-25 review).
  const actionable = (n.actions?.length ?? 0) > 0;
  if (n.jobId === null || !actionable) return base;

  // A relative href needs an origin to parse; it is discarded again below.
  const url = new URL(base, 'https://local');
  url.searchParams.set(FLASH_PARAM, String(n.jobId));
  return `${url.pathname}${url.search}`;
}

/**
 * The arrival marker's parameter name.
 *
 * ⚠️ Deliberately NOT a boolean. It carries the JOB id, because a picklist can have several posting
 * batches in flight at once and "flash the posting surface" is ambiguous there — the MIS page needs
 * to know WHICH batch the user was chased about.
 */
export const FLASH_PARAM = 'flash';
